<script setup lang="ts">
import { computed } from 'vue';
import { useStore } from '@/composables/useVuexHelpers';
import OutlinePanel from '@/components/outline/OutlinePanel.vue';
import ArticlePanel from '@/components/article/ArticlePanel.vue';
import RoundStatus from '@/assets/js/enum/RoundStatus';

interface Props {
  outlineSource?: Record<string, unknown>;
}

interface Emits {
  (e: 'scroll', event: Event): void;
  (e: 'textClick', data: unknown): void;
  (e: 'markupClick', node: unknown): void;
}

const _props = withDefaults(defineProps<Props>(), {
  outlineSource: undefined,
});

const emit = defineEmits<Emits>();

const store = useStore();

const mainPanelView = computed(() => store.state.mainPanelView);
const UI = computed(() => store.state.UI);
const canEditOutline = computed(() => store.getters.canEditOutline);
const roundStatus = computed(() => store.getters.roundStatus);
const documentSelectedSource = computed(() => store.getters.documentSelectedSource);

function doSidePanelScroll(ev: Event) {
  emit('scroll', ev);
  store.commit('incrementScrollCounter');
}

function handleTextSelection(data: unknown) {
  if (canEditOutline.value) {
    emit('textClick', data);
  }
}

function handleMarkupClick(node: unknown) {
  emit('markupClick', node);
}

function doMouseEnter() {
  store.commit('setScrollSource', 'sidePanel');
}

function doMouseLeave() {
  store.commit('setScrollSource', 'window');
}
</script>

<template>
  <div
    class="side-panel uk-background-muted uk-position-fixed"
    :class="[
      canEditOutline || roundStatus === RoundStatus.EDITING_OUTLINED_ANALYZED
        ? 'sidePanelWidth'
        : 'uk-width-1-3',
      { 'sidepanel-showbar': UI.showSidebar },
    ]"
    @scroll="doSidePanelScroll"
    @mouseenter="doMouseEnter"
    @mouseleave="doMouseLeave"
  >
    <OutlinePanel
      v-if="
        roundStatus === RoundStatus.EDITING_NLP_OUTLINE ||
        roundStatus === RoundStatus.EDITING_OUTLINED_ANALYZED
          ? mainPanelView !== 'article'
          : mainPanelView !== 'outline'
      "
      class="uk-height-1-1 min-height-custom"
      :outlineSource="outlineSource"
      @textClick="handleTextSelection"
      @markupClick="handleMarkupClick"
    />
    <ArticlePanel
      v-if="
        roundStatus === RoundStatus.EDITING_NLP_OUTLINE ||
        roundStatus === RoundStatus.EDITING_OUTLINED_ANALYZED
          ? mainPanelView !== 'outline'
          : mainPanelView !== 'article'
      "
      class="uk-height-1-1 uk-height-viewport"
      :mode="'none'"
      :article="documentSelectedSource"
      @textClick="handleTextSelection"
      @markupClick="handleMarkupClick"
    />
  </div>
</template>

<style lang="scss" scoped>
.side-panel {
  max-height: calc(100vh - 165px);
  overflow-y: scroll;
  overflow-x: hidden;
  box-shadow:
    0 4px 4px rgb(0 0 0 / 25%),
    0 5px 10px rgb(0 0 0 / 25%);
}

.sidePanelWidth {
  width: 45vw;
}

.sidepanel-showbar {
  padding-top: 1.5rem;
}

.min-height-custom {
  min-height: calc(100vh - 180px) !important;
}
</style>
